import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useBalance, useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { BIGCOINMAIN_ADDRESS, RoomProperties, TOKEN_SYMBOL } from "../../../utils/constants";
import { formatUnits, zeroAddress } from "viem";
import BigcoinMainContractABI from "../../../abis/bigcoinmain.abi.json";
import { notifyError, notifySuccess } from "../../../hooks/utils";
import styled from "styled-components";
import "./buyfarmmodal.css"
import { getCookie, isValidChecksumAddress } from "../../../utils";
import { useTranslation } from "react-i18next";

interface BuyFarmModalProps {
  open: boolean;
  onClose: () => void;
}

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 0.5rem;
  padding: 1rem;
  background: rgb(26, 61, 79);
`;

const FarmInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 1rem;
  padding: 1.5rem;
  border: 3px solid rgb(26, 61, 79);
  width: 100%;
`;

const CancelButton = styled.button`
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    font-weight: bold;
    text-transform: uppercase;
    font-family: inherit;
    background: rgb(16, 44, 57);
    color: #507c83;
    border: 2px solid #507c83;
    opacity: 1;
    transition: 0.2s ease-in-out, transform 0.1s ease-in-out;

    &:hover {
       background: rgb(26, 61, 79);
    }
`;
const BuyButton = styled.button`
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    font-weight: bold;
    text-transform: uppercase;
    font-family: inherit;
    background: #507c83;
    color: rgb(16, 44, 57);
    border: none;
    opacity: 1;
    transition: 0.2s ease-in-out, transform 0.1s ease-in-out;

    &:hover {
       background: #24c7d6;
    }
`;

const BuyFarmModal: React.FC<BuyFarmModalProps> = ({ open, onClose }) => {
const { t, i18n } = useTranslation();
  const account = useAccount();

  const userBalance = useBalance({
          address: account ? account.address : zeroAddress,
      })

  const [placingTrade, setPlacingTrade] = useState(false);

  const { data: hash, error, isPending, writeContract } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  // Log the transaction arguments and hash for debugging
  useEffect(() => {
    if (hash) {
      notifySuccess("Buying new barn!");
    }
    if (error) {
      notifyError("An error occurred while trying to buy new barn.");
    }
    if (isConfirmed) {
      notifySuccess("Bought new barn!");
    }
  }, [hash, error]);

  const referrer = useMemo(() => {
    const cookieRef = getCookie("ref");
    const checksummed = isValidChecksumAddress(cookieRef);
    return checksummed ?? zeroAddress; // fallback to zero address if invalid
  }, []);

  const { data: initialFacilityPrice } = useReadContract({
    abi: BigcoinMainContractABI,
    address: BIGCOINMAIN_ADDRESS,
    functionName: 'initialFacilityPrice',
    args: [], // args is an array of arguments to pass to the function
  });

  const userHasEnoughS = useMemo(() => {
    if (userBalance && userBalance.data && initialFacilityPrice) {
      const userSBalance = userBalance.data.value?.toString() ?? "0";
      const facilityPrice = formatUnits(initialFacilityPrice.toString() as any, 18);
      return Number(userSBalance) >= Number(facilityPrice);
    }
    return false;
  }, [userBalance, initialFacilityPrice]);

  const handleBuyNewFarm = async () => {
    if (!account) {
      // Redirect to login or show a message
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setPlacingTrade(true);

      const transactionArgs = {
        address: BIGCOINMAIN_ADDRESS,
        abi: BigcoinMainContractABI,
        functionName: 'purchaseInitialFacility',
        args: [referrer],
        value: initialFacilityPrice, // 100 S
      };

      writeContract(transactionArgs as any);
    } catch (error) {
      console.log("Error in handleBuyNewFarm:", error, "referrer:", referrer);
      notifyError("An error occurred while trying to buy new barn.");
    } finally {
      setPlacingTrade(false);
    }
  };


  // MODAL OPEN AND CLOSE
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div data-state="open" className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ pointerEvents: "auto" }} data-aria-hidden="true" aria-hidden="true">
      <div
        ref={modalRef}
        role="dialog"
        id="radix-:r4f:"
        aria-describedby="radix-:r4h:"
        aria-labelledby="radix-:r4g:"
        data-state="open"
        className="text-sm fixed left-[50%] top-[50%] z-50 grid w-full max-w-modal translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950 bg-primary text-white"
        tabIndex={-1}
        style={{ pointerEvents: "auto", border: "none", background: "rgb(16, 44, 57)" }}
      >


        <div style={{ position: "relative", zIndex: "1", color: "white", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "stretch", gap: "1.5rem", color: "rgb(220, 228, 228)", padding: "12px" }}>
            <h2 style={{ textAlign: "center", margin: "0px", color: "rgb(220, 228, 228)", fontSize: "16px" }}>{t("buy_first_barn.title")}</h2>
            <InfoBox className="sc-715ed997-2 pznlJ">
              <p className="sc-715ed997-3 iEELuk">
                {t("buy_first_barn.description")}
              </p>
            </InfoBox>
            <div className="sc-715ed997-4 bUtEUQ">
              <FarmInfoBox className="sc-715ed997-5 lnGjEF">
                <h3 className="sc-715ed997-6 dxmyGS">{t("buy_first_barn.setup_shop")} {RoomProperties[1].name}</h3>
                <div className="sc-715ed997-7 kUreYw">
                  <img
                    src={RoomProperties[1].img}
                    alt={`SET UP SHOP IN YOUR ${RoomProperties[1].name}`}
                    width={120}
                    height={120}
                    style={{ objectFit: "contain" }}
                  />
                </div>
                <div className="sc-715ed997-8 llMjcT" style={{ textAlign: "left" }}>
                  <p className="sc-715ed997-9 ihbpoj">{t("buy_first_barn.power_output")}: {RoomProperties[1].maxPower} {t("buy_first_barn.food")}</p>
                  <p className="sc-715ed997-9 ihbpoj">{t("buy_first_barn.space_capacity")}: {RoomProperties[1].maxMiners} {t("buy_first_barn.units")}</p>
                  <p className="sc-715ed997-9 ihbpoj">{t("buy_first_barn.price")}: {RoomProperties[1].cost} S</p>
                  <div className="sc-715ed997-10 MTHWI">
                    <p className="sc-715ed997-11 hgrPaE">
                      {t("buy_first_barn.free_miner_included")}
                    </p>
                  </div>
                </div>
              </FarmInfoBox>
            </div>
            <div className="sc-715ed997-12 bZYtMs">
              <CancelButton onClick={onClose}>{t("buy_first_barn.cancel")}</CancelButton>
              <BuyButton
                disabled={placingTrade || isPending || isConfirming || !userHasEnoughS}
                onClick={() => handleBuyNewFarm()}>
                {(placingTrade || isPending || isConfirming) ? t("buy_first_barn.purchasing") : t("buy_first_barn.purchase_now")}</BuyButton>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
export default BuyFarmModal;